import { z } from "zod";
import { opaqueCursorSchema, opaqueIdSchema, sha256Schema } from "./opaque.js";
export const archiveItemKindSchema = z.enum(["TEXT", "AUDIO", "VIDEO", "PHOTO", "DOCUMENT"]);
export const archiveSourceKindSchema = z.enum([
    "CHAPTER_REVISION",
    "MEDIA_ASSET",
    "BOOK_PREVIEW",
    "STUDIO_EXPORT",
    "IMPORTED",
]);
export const archiveProvenanceSchema = z
    .object({
    sourceKind: archiveSourceKindSchema,
    sourceId: opaqueIdSchema,
    sourceRevision: z.number().int().nonnegative().nullable(),
    checksumSha256: sha256Schema.nullable(),
    recordedAt: z.string().datetime(),
})
    .strict();
export const archivePrivacySchema = z.enum(["OWNER_ONLY", "BOOK_MEMBERS", "EXPLICIT_GRANTS"]);
export const archiveGrantKindSchema = z.enum(["ARCHIVE_ACCESS", "PLAYBACK", "QR"]);
export const archiveGrantStateSchema = z.enum(["ACTIVE", "REVOKED", "EXPIRED"]);
export const archiveGrantSchema = z
    .object({
    grantId: opaqueIdSchema,
    itemId: opaqueIdSchema,
    granteeId: opaqueIdSchema,
    kind: archiveGrantKindSchema,
    state: archiveGrantStateSchema,
    grantedAt: z.string().datetime(),
    expiresAt: z.string().datetime().nullable(),
    revokedAt: z.string().datetime().nullable(),
})
    .strict()
    .superRefine((value, context) => {
    if (value.state === "REVOKED" && value.revokedAt === null)
        context.addIssue({ code: "custom", path: ["revokedAt"], message: "Required when revoked." });
    if (value.state !== "REVOKED" && value.revokedAt !== null)
        context.addIssue({
            code: "custom",
            path: ["revokedAt"],
            message: "Only valid when revoked.",
        });
});
export const archiveGrantSummarySchema = z
    .object({
    active: z.number().int().nonnegative(),
    revokedOrExpired: z.number().int().nonnegative(),
})
    .strict();
export const archiveRetentionStateSchema = z.enum(["RETAIN", "DELETION_SCHEDULED", "DELETED"]);
export const archiveRetentionSchema = z
    .object({
    state: archiveRetentionStateSchema,
    deleteAfter: z.string().datetime().nullable(),
    deletedAt: z.string().datetime().nullable(),
})
    .strict()
    .superRefine((value, context) => {
    if (value.state === "DELETION_SCHEDULED" && value.deleteAfter === null)
        context.addIssue({
            code: "custom",
            path: ["deleteAfter"],
            message: "Required when deletion is scheduled.",
        });
    if (value.state === "DELETED" && value.deletedAt === null)
        context.addIssue({ code: "custom", path: ["deletedAt"], message: "Required when deleted." });
    if (value.state === "RETAIN" && (value.deleteAfter !== null || value.deletedAt !== null))
        context.addIssue({
            code: "custom",
            path: ["state"],
            message: "Retained items cannot carry deletion timestamps.",
        });
});
export const archiveDerivedTextStatusSchema = z.enum([
    "NOT_APPLICABLE",
    "PENDING",
    "READY",
    "NEEDS_ATTENTION",
    "DELETED",
]);
export const archivePlaybackStateSchema = z.enum(["UNAVAILABLE", "READY", "NEEDS_ATTENTION"]);
export const archiveQrGrantStateSchema = z.enum(["DISABLED", "ACTIVE", "REVOKED", "EXPIRED"]);
export const archivePlaybackSchema = z
    .object({
    state: archivePlaybackStateSchema,
    qrGrantState: archiveQrGrantStateSchema,
    qrGrantId: opaqueIdSchema.nullable(),
})
    .strict()
    .superRefine((value, context) => {
    if (value.qrGrantState === "ACTIVE" && value.qrGrantId === null)
        context.addIssue({
            code: "custom",
            path: ["qrGrantId"],
            message: "An active QR grant requires an opaque grant ID.",
        });
    if (value.qrGrantState === "DISABLED" && value.qrGrantId !== null)
        context.addIssue({
            code: "custom",
            path: ["qrGrantId"],
            message: "A disabled QR grant cannot expose a grant ID.",
        });
});
export const archiveItemSchema = z
    .object({
    itemId: opaqueIdSchema,
    bookId: opaqueIdSchema,
    kind: archiveItemKindSchema,
    label: z.string().trim().min(1).max(200),
    provenance: archiveProvenanceSchema,
    privacy: archivePrivacySchema,
    grantSummary: archiveGrantSummarySchema,
    retention: archiveRetentionSchema,
    transcriptStatus: archiveDerivedTextStatusSchema,
    captionStatus: archiveDerivedTextStatusSchema,
    playback: archivePlaybackSchema,
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
})
    .strict();
export const archivePageInfoSchema = z
    .object({ nextCursor: opaqueCursorSchema.nullable(), hasMore: z.boolean() })
    .strict()
    .superRefine((value, context) => {
    if (value.hasMore !== (value.nextCursor !== null))
        context.addIssue({
            code: "custom",
            path: ["nextCursor"],
            message: "nextCursor must be present exactly when hasMore is true.",
        });
});
export const archivePageQuerySchema = z
    .object({
    cursor: opaqueCursorSchema.optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
})
    .strict();
export const archiveItemPageSchema = z
    .object({ items: z.array(archiveItemSchema).max(100), page: archivePageInfoSchema })
    .strict();
export const archiveItemPageResponseSchema = z.object({ data: archiveItemPageSchema }).strict();
export const archiveGrantPageSchema = z
    .object({ items: z.array(archiveGrantSchema).max(100), page: archivePageInfoSchema })
    .strict();
export const archiveGrantPageResponseSchema = z.object({ data: archiveGrantPageSchema }).strict();
export const archiveAnalyticsEventNameSchema = z.enum([
    "ARCHIVE_OPENED",
    "ARCHIVE_ITEM_OPENED",
    "PLAYBACK_STARTED",
    "PLAYBACK_COMPLETED",
    "QR_GRANT_CREATED",
    "QR_GRANT_REVOKED",
]);
const contentFreeCodeSchema = z
    .string()
    .trim()
    .min(1)
    .max(80)
    .regex(/^[a-z0-9][a-z0-9_.:-]*$/i, "Expected a content-free status code");
export const archiveAnalyticsEventSchema = z
    .object({
    eventId: opaqueIdSchema,
    name: archiveAnalyticsEventNameSchema,
    occurredAt: z.string().datetime(),
    bookId: opaqueIdSchema,
    itemId: opaqueIdSchema.nullable(),
    itemKind: archiveItemKindSchema.nullable(),
    durationMs: z.number().int().nonnegative().max(86_400_000).nullable(),
    status: contentFreeCodeSchema.nullable(),
})
    .strict();
//# sourceMappingURL=archive.js.map